import React, { useMemo, useState, useCallback, useEffect } from "react";
import Table from "../../../components/Table";
import TitleButtons from "../../../components/TitleButtons";
import LeftTable from "../../../components/LeftTable";
import { useStandbySuprimentos } from "./standbyData";
import Modal from "../../../components/Modal";
import ModalItemsGrid from "../../../components/ModalItemsGrid";
import AuvoReportsSection from "../../../components/integrations/AuvoReportsSection";
import { useAuth } from "../../../context/auth-context";

type Row = (string | number | React.ReactNode)[];

const IDX = {
  OS: 0,
  RESP_COORD: 1, // C. Responsável
  CODIGO: 2,
  NOME: 3,
  QTD: 4,
  CUSTO: 5, // Preço de Venda
  TECNICO: 6, // R. Coleta
  DATA: 7,
  HORA: 8,
};

type RawProduto = { codigo: string; nome: string; posicao?: string | null };
type RawItem = {
  id: number;
  movimentacao_id: number;
  produto_id: number;
  quantidade: number;
  custo_unitario: string | null;
  produto: RawProduto;
  preco_venda?: string | number | null;
  moeda?: string | null;
  pendente_retorno?: boolean | null;
};

type RawEmpresa = {
  id: string;
  nome_fantasia?: string | null;
  responsaveis_ids?: number[] | null;
  responsaveis_nomes?: string[] | null;
};

type RawMov = {
  movimentacao: {
    id: number;
    responsavel_id: number | null;
    responsavel_nome: string | null;
    departamento_origem: string | null;
    tipo: string | null;
    os_numero?: string | null;
    coletor_nome?: string | null;
    criado_em: string;
    embarcacao?: string | null;
    comentario?: string | null;
    pendente_retorno?: boolean | null;
    empresa?: RawEmpresa | null;
  };
  itens: RawItem[];
};

const API_BASE = "https://api.eva-googlemarine.com";
const URL_LIST = `${API_BASE}/departamentos/coordenacao/standby`;
const URL_BAIXAR = `${API_BASE}/movimentar-produtos-baixar`;
const URL_ENVALIDAR = `${API_BASE}/departamentos/suprimentos/produtos/invalidate`;
const URL_AJUSTAR_PENDENTE = `${API_BASE}/departamentos/coordenacao/ajustar-pendente`;

/* ======================= Helpers ======================= */
function isZeroLike(v: unknown): boolean {
  if (v == null) return true;
  if (typeof v === "number") return Math.abs(v) < 1e-9;
  const s = String(v).trim();
  if (!s) return true;
  const n = Number(
    s
      .replace(/[^\d,.-]/g, "")
      .replace(/\./g, "")
      .replace(",", ".")
  );
  return Number.isFinite(n) && Math.abs(n) < 1e-9;
}

function toStartOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}
function parseDateSmart(s: string): Date | null {
  if (!s) return null;
  const m = /^(\d{2})\/(\d{2})\/(\d{4})/.exec(s.trim());
  if (m) {
    const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
    return isNaN(d.getTime()) ? null : d;
  }
  const d2 = new Date(s);
  return isNaN(d2.getTime()) ? null : d2;
}
function diffDaysFromToday(dateStr: string): number | null {
  const d = parseDateSmart(dateStr);
  if (!d) return null;
  const ms = toStartOfDay(new Date()).getTime() - toStartOfDay(d).getTime();
  return Math.max(0, Math.floor(ms / 86400000));
}

function firstTwoNames(full?: string | null): string {
  if (!full) return "-";
  const parts = full.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "-";
  return parts.slice(0, 2).join(" ");
}

/* ======================= Célula OS com “nota” ======================= */
const OsNoteCell: React.FC<{ os: string; comment?: string | null }> = ({
  os,
  comment,
}) => {
  const has = Boolean(comment && comment.trim());
  if (!has) return <span>{os}</span>;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        position: "relative",
      }}
    >
      <span>{os}</span>
      <button
        type="button"
        tabIndex={0}
        aria-label="Ver comentário"
        onDoubleClick={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        style={{
          all: "unset",
          cursor: "default",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 17,
          height: 17,
          borderRadius: 14,
          background: "#E5E7EB00",
          color: "#BC5016FF",
          fontSize: 14,
          lineHeight: "14px",
          fontWeight: 700,
          transform: "translateY(-1px)",
        }}
      >
        •
        <span
          className="os-note-tooltip"
          style={{
            position: "absolute",
            bottom: "100%",
            left: 0,
            transform: "translateY(-8px)",
            background: "#111827",
            color: "#fff",
            fontSize: 12,
            lineHeight: 1.35,
            padding: "8px 10px",
            borderRadius: 8,
            boxShadow: "0 10px 24px rgba(0,0,0,.25)",
            maxWidth: 420,
            minWidth: 180,
            opacity: 0,
            pointerEvents: "none",
            whiteSpace: "pre-wrap",
            transition: "opacity 140ms ease",
            zIndex: 2,
          }}
        >
          {comment}
        </span>
      </button>
      <style>{`
        button:focus .os-note-tooltip,
        button:hover .os-note-tooltip { opacity: 1; }
        button:hover { background: #dbe2ea; color: #374151; }
        button:focus { outline: 2px solid #93c5fd; outline-offset: 2px; }
      `}</style>
    </span>
  );
};

/* ======================= Página ======================= */
const CoordenacaoStandby: React.FC = () => {
  const headers = useMemo(
    () => [
      "O.S.",
      "C. Responsável",
      "Código",
      "Nome do Produto",
      "Quantidade",
      "Preço de Venda",
      "R. Coleta",
      "Data",
      "Dias de Atraso",
    ],
    []
  );

  const columnWidths = useMemo(
    () => [
      "60px",
      "120px",
      "70px",
      "300px",
      "10px",
      "120px",
      "120px",
      "80px",
      "70px",
    ],
    []
  );

  const { rows, refresh, rawByOS } = useStandbySuprimentos();

  const [openOS, setOpenOS] = useState<string | null>(null);
  const [rawMovs, setRawMovs] = useState<RawMov[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  // ==== auth: nome real do usuário logado ====
  const { sessao } = useAuth();
  const meuNomeCoordenador = useMemo(() => {
    if (sessao?.nome && typeof sessao.nome === "string") {
      return sessao.nome.trim();
    }
    return "";
  }, [sessao]);

  // ✅ DEV: permitir baixa em qualquer OS
  const isDev = useMemo(() => {
    // Ajuste aqui conforme seu auth real (mantive várias opções comuns)
    const dep = String((sessao as any)?.departamento || "")
      .toLowerCase()
      .trim();
    const role = String((sessao as any)?.role || "")
      .toLowerCase()
      .trim();
    const is_dev_flag =
      (sessao as any)?.is_dev === true || (sessao as any)?.isDev === true;

    if (is_dev_flag) return true;
    if (dep === "desenvolvedor" || dep === "dev") return true;
    if (role === "desenvolvedor" || role === "dev" || role === "developer")
      return true;

    // Se você tiver algo como sessao.scopes (array)
    const scopes = (sessao as any)?.scopes;
    if (
      Array.isArray(scopes) &&
      scopes.some((s: any) => String(s).toLowerCase() === "dev")
    )
      return true;

    return false;
  }, [sessao]);

  // ==== /auth/me (uid) ====
  const [meId, setMeId] = useState<number | null>(null);
  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/auth/me`, {
          method: "GET",
          credentials: "include",
          headers: { Accept: "application/json" },
          signal: ac.signal,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json().catch(() => ({}));
        const cand =
          json?.sessao?.uid ?? json?.user?.id ?? json?.uid ?? json?.id ?? null;
        const n = Number(cand);
        setMeId(Number.isFinite(n) ? n : null);
      } catch {
        setMeId(null);
      }
    })();
    return () => ac.abort();
  }, []);

  const ensureMeId = useCallback(async () => {
    if (meId != null) return meId;
    try {
      const res = await fetch(`${API_BASE}/auth/me`, {
        method: "GET",
        credentials: "include",
        headers: { Accept: "application/json" },
      });
      if (!res.ok) return null;
      const json = await res.json().catch(() => ({}));
      const cand =
        json?.sessao?.uid ?? json?.user?.id ?? json?.uid ?? json?.id ?? null;
      const n = Number(cand);
      const uid = Number.isFinite(n) ? n : null;
      if (uid != null) setMeId(uid);
      return uid;
    } catch {
      return null;
    }
  }, [meId]);

  const invalidateProdutos = useCallback(async () => {
    try {
      await fetch(URL_ENVALIDAR, {
        method: "POST",
        headers: { Accept: "application/json" },
      });
    } catch (e) {
      console.warn("Falha ao invalidar produtos (ignorado):", e);
    }
  }, []);

  // comentário por OS
  const getOsComment = useCallback(
    (os: string): string | null => {
      if (!os || !rawByOS) return null;
      const entry = (rawByOS as any)[os];
      const c = entry?.mov?.comentario;
      return typeof c === "string" && c.trim() ? c.trim() : null;
    },
    [rawByOS]
  );

  // pendência por OS
  const isOsPendente = useCallback(
    (os: string): boolean => {
      const entry = (rawByOS as any)?.[os];
      if (!entry) return false;
      if (entry.mov?.pendente_retorno === true) return true;
      const itens = entry.itens || [];
      return itens.some((it: any) => it?.pendente_retorno === true);
    },
    [rawByOS]
  );

  // 🔹 Ordena e AGRUPA por O.S.:
  const groupedRowsByOS = useMemo(() => {
    const base = (rows as Row[]) || [];
    if (!base.length) return base;

    const lowerName = meuNomeCoordenador.trim().toLowerCase();
    type OsGroup = {
      os: string;
      rows: Row[];
      respLower: string;
      firstIndex: number;
    };

    const map = new Map<string, OsGroup>();

    base.forEach((r, idx) => {
      const os = String(r[IDX.OS] ?? "").trim() || "-";
      const respLower = String(r[IDX.RESP_COORD] ?? "")
        .trim()
        .toLowerCase();
      const existing = map.get(os);
      if (existing) {
        existing.rows.push(r);
      } else {
        map.set(os, {
          os,
          rows: [r],
          respLower,
          firstIndex: idx,
        });
      }
    });

    const groups = Array.from(map.values());

    groups.sort((a, b) => {
      if (lowerName) {
        const aMine = a.respLower === lowerName;
        const bMine = b.respLower === lowerName;
        if (aMine !== bMine) return aMine ? -1 : 1;
      }
      return a.firstIndex - b.firstIndex;
    });

    const out: Row[] = [];
    for (const g of groups) out.push(...g.rows);
    return out;
  }, [rows, meuNomeCoordenador]);

  // LISTA: linhas visíveis
  const tableDisplayRows = useMemo(() => {
    return groupedRowsByOS.map((r) => {
      const clone = r.slice() as Row;

      if (
        String(clone[IDX.CUSTO]).toLowerCase() !== "pendente informar" &&
        isZeroLike(clone[IDX.CUSTO])
      ) {
        clone[IDX.CUSTO] = "pendente informar";
      }

      const osStr = String(r[IDX.OS] ?? "");
      const cmt = getOsComment(osStr);
      clone[IDX.OS] = cmt ? <OsNoteCell os={osStr} comment={cmt} /> : osStr;

      clone[IDX.TECNICO] = firstTwoNames(String(r[IDX.TECNICO] ?? "-"));

      if (clone.length > IDX.HORA) clone.splice(IDX.HORA, 1);

      const dataStr = String(r[IDX.DATA] ?? "");
      const days = diffDaysFromToday(dataStr);
      clone.push(days == null ? "-" : String(days));

      return clone;
    });
  }, [groupedRowsByOS, getOsComment]);

  // extrair OS de uma célula
  const getOsFromArbitraryCell = (cell: any): string => {
    if (typeof cell === "string" || typeof cell === "number")
      return String(cell);
    if (
      cell &&
      typeof cell === "object" &&
      "props" in cell &&
      (cell as any).props?.os
    )
      return String((cell as any).props.os);
    return "";
  };

  const getOsFromDisplayRowForPending = useCallback(
    (row: (string | number | React.ReactNode)[] | undefined): string => {
      if (!row) return "";
      const v = row[IDX.OS] as any;
      return getOsFromArbitraryCell(v);
    },
    []
  );

  // duplo clique abre modal
  const handleDoubleClick = useCallback(
    (_viewIndex: number, rowData: (string | number | React.ReactNode)[]) => {
      const os = getOsFromDisplayRowForPending(rowData as Row).trim();
      if (!os) return;
      setOpenOS(os);
    },
    [getOsFromDisplayRowForPending]
  );

  // carrega RAW quando modal abre
  useEffect(() => {
    let cancelled = false;
    async function loadRaw() {
      if (!openOS) return;
      setBusy(false);
      setSelectedIndex(null);

      try {
        const res = await fetch(URL_LIST, {
          headers: { Accept: "application/json" },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const data = Array.isArray(json?.data) ? (json.data as RawMov[]) : [];
        const onlyThisOS = data.filter(
          (m) => (m.movimentacao.os_numero || "-") === openOS
        );
        if (!cancelled) setRawMovs(onlyThisOS);
      } catch (e) {
        console.error("Falha ao carregar RAW da O.S.:", e);
        if (!cancelled) setRawMovs(null);
      }
    }
    loadRaw();
    return () => {
      cancelled = true;
    };
  }, [openOS]);

  const embarcacaoNome = useMemo(() => {
    if (!rawMovs?.length) return "-";
    const mv = rawMovs.find((m) => (m.movimentacao.embarcacao || "").trim());
    return (mv?.movimentacao.embarcacao || "-").trim();
  }, [rawMovs]);

  const empresaNome = useMemo(() => {
    if (!rawMovs?.length) return "-";
    const mv = rawMovs.find(
      (m) =>
        m.movimentacao.empresa &&
        (m.movimentacao.empresa.nome_fantasia || "").trim()
    );
    return (mv?.movimentacao.empresa?.nome_fantasia || "-").trim();
  }, [rawMovs]);

  const osComment = useMemo(
    () => (openOS ? getOsComment(openOS) : null),
    [openOS, getOsComment]
  );

  const osIsPendente = useMemo(
    () => (openOS ? isOsPendente(openOS) : false),
    [openOS, isOsPendente]
  );

  function tipoLabel(tipo: string | null | undefined): string {
    if (!tipo) return "-";
    if (tipo.includes("servico")) return "Serviço";
    if (tipo.includes("venda")) return "Venda";
    return tipo;
  }

  const osDetails = useMemo(() => {
    if (!openOS) return null;
    const itens = (rows as Row[]).filter((r) => String(r[IDX.OS]) === openOS);
    const entry = (rawByOS as any)?.[openOS];
    if (!itens.length)
      return {
        info: [] as { label: string; value: string }[],
        headers: [] as string[],
        tableRows: [] as Row[],
        colWidths: [] as string[],
      };

    const first = itens[0];
    const data = String(first[IDX.DATA] ?? "-");
    const hora = String(first[IDX.HORA] ?? "-");
    const tecnico = String(first[IDX.TECNICO] ?? "-");

    const info: { label: string; value: string }[] = [
      { label: "Número da O.S", value: openOS },
      { label: "Empresa", value: empresaNome || "-" },
      { label: "Embarcação", value: embarcacaoNome || "-" },
      { label: "Responsável/Técnico", value: tecnico || "-" },
      { label: "Data", value: data },
      { label: "Hora", value: hora },
    ];
    if (osComment) info.unshift({ label: "Comentário", value: osComment });

    const detailsHeaders = [
      "Tipo",
      "Código",
      "Nome do Produto",
      "Quantidade",
      "Preço de Venda",
      "Moeda",
      "Responsável pela Coleta",
      "Data",
      "Hora",
    ];
    const detailsColWidths = [
      "80px",
      "100px",
      "300px",
      "110px",
      "180px",
      "80px",
      "190px",
      "100px",
      "100px",
    ];
    const tipoStr = tipoLabel(entry?.mov?.tipo);

    const tableRows: Row[] = itens.map((r) => [
      String(tipoStr ?? "-"),
      String(r[IDX.CODIGO] ?? "-"),
      String(r[IDX.NOME] ?? "-"),
      String(r[IDX.QTD] ?? "-"),
      "-",
      "-",
      String(r[IDX.TECNICO] ?? "-"),
      String(r[IDX.DATA] ?? "-"),
      String(r[IDX.HORA] ?? "-"),
    ]);

    return {
      info,
      headers: detailsHeaders,
      colWidths: detailsColWidths,
      tableRows,
    };
  }, [openOS, rows, embarcacaoNome, empresaNome, osComment, rawByOS]);

  // === checa se a O.S. aberta é "minha" (pelo C. Responsável) ===
  const osEhMinha = useMemo(() => {
    if (!openOS || !meuNomeCoordenador) return false;
    const base = (rows as Row[]) || [];
    const lower = meuNomeCoordenador.toLowerCase();
    const found = base.find((r) => String(r[IDX.OS]) === openOS);
    if (!found) return false;
    const resp = String(found[IDX.RESP_COORD] ?? "")
      .trim()
      .toLowerCase();
    return resp === lower;
  }, [openOS, meuNomeCoordenador, rows]);

  // ✅ NOVO: permissão final para baixa
  const podeBaixarEstaOS = useMemo(() => {
    return osEhMinha || isDev;
  }, [osEhMinha, isDev]);

  async function postJSON(url: string, payload: any) {
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const t = await res.text().catch(() => "");
      throw new Error(`HTTP ${res.status} – ${t || "erro ao enviar"}`);
    }
    return res.json().catch(() => ({}));
  }

  async function askQuantidade(max: number): Promise<number | null> {
    const val = window.prompt(
      `Quantidade (máxima: ${max})`,
      String(Math.min(1, max))
    );
    if (val == null) return null;
    const n = Number(String(val).replace(",", "."));
    if (!Number.isFinite(n) || n <= 0)
      return window.alert("Informe um número válido maior que zero."), null;
    if (!Number.isInteger(n))
      return window.alert("A quantidade deve ser inteira."), null;
    if (n > max)
      return window.alert(`A quantidade não pode exceder ${max}.`), null;
    return n;
  }

  const ensureRawForOS = useCallback(
    async (osNumero: string): Promise<RawMov[] | null> => {
      if (rawMovs && rawMovs.length) return rawMovs;
      try {
        const res = await fetch(URL_LIST, {
          headers: { Accept: "application/json" },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const data = Array.isArray(json?.data) ? (json.data as RawMov[]) : [];
        const onlyThisOS = data.filter(
          (m) => (m.movimentacao.os_numero || "-") === osNumero
        );
        setRawMovs(onlyThisOS);
        return onlyThisOS;
      } catch (e) {
        console.error("Falha ao carregar RAW on-demand:", e);
        return null;
      }
    },
    [rawMovs]
  );

  function mapUiToRawItem(data: RawMov[], uiRow: Row): RawItem | null {
    const codigo = String(uiRow[1] ?? "-");
    const nome = String(uiRow[2] ?? "-");
    const qtd = Number(uiRow[3] ?? 0);

    for (const m of data)
      for (const it of m.itens || [])
        if (
          it.produto?.codigo === codigo &&
          it.produto?.nome === nome &&
          Number(it.quantidade) === qtd
        )
          return it;

    for (const m of data)
      for (const it of m.itens || [])
        if (it.produto?.codigo === codigo && Number(it.quantidade) === qtd)
          return it;

    for (const m of data)
      for (const it of m.itens || [])
        if (it.produto?.codigo === codigo) return it;

    return null;
  }

  // ✅ TROCA: agora dev pode baixar qualquer OS
  const baixarOsEnabled = !!openOS && !busy && podeBaixarEstaOS;
  const baixarItemEnabled = selectedIndex != null && !busy && podeBaixarEstaOS;

  const handleBaixarOSInteira = useCallback(async () => {
    if (!openOS || !baixarOsEnabled) return;

    try {
      setBusy(true);
      const data = await ensureRawForOS(openOS);
      if (!data || !data.length)
        return window.alert("Dados da O.S. indisponíveis para baixa.");

      const uid = await ensureMeId();
      if (uid == null)
        return window.alert(
          "Sessão não encontrada. Faça login para prosseguir."
        );

      const itens = data
        .flatMap((m) => m.itens || [])
        .filter((it) => Number(it.quantidade) > 0)
        .map((it) => ({
          movimentacao_id: it.movimentacao_id,
          produto_id: it.produto_id,
          quantidade: Number(it.quantidade),
        }));

      if (!itens.length)
        return window.alert("Não há itens com quantidade para dar baixa.");

      await postJSON(URL_BAIXAR, { responsavel_id: uid, itens });
      await invalidateProdutos();
      window.alert("Todos os itens tiveram baixa realizada.");
      await refresh?.();
      setOpenOS(null);
    } catch (e: any) {
      window.alert(`Falha ao dar baixa na O.S.: ${e?.message || e}`);
    } finally {
      setBusy(false);
    }
  }, [
    openOS,
    baixarOsEnabled,
    ensureRawForOS,
    ensureMeId,
    refresh,
    invalidateProdutos,
  ]);

  const handleBaixarItem = useCallback(async () => {
    if (!openOS || !osDetails || !baixarItemEnabled) return;

    try {
      setBusy(true);
      const data = await ensureRawForOS(openOS);
      if (!data || !data.length)
        return window.alert("Dados da O.S. indisponíveis.");

      const ui = osDetails.tableRows[selectedIndex as number];
      if (!ui) return window.alert("Item selecionado não encontrado.");

      const rawItem = mapUiToRawItem(data, ui);
      if (!rawItem)
        return window.alert("Não foi possível localizar o item selecionado.");

      const max = Number(rawItem.quantidade ?? 0);
      if (!(max > 0))
        return window.alert("Item sem quantidade disponível para baixa.");

      const qtd = await askQuantidade(max);
      if (qtd == null) return;

      const uid = await ensureMeId();
      if (uid == null)
        return window.alert(
          "Sessão não encontrada. Faça login para prosseguir."
        );

      const payload = {
        responsavel_id: uid,
        itens: [
          {
            movimentacao_id: rawItem.movimentacao_id,
            produto_id: rawItem.produto_id,
            quantidade: qtd,
          },
        ],
      };

      await postJSON(URL_BAIXAR, payload);
      await invalidateProdutos();
      window.alert("Baixa realizada com sucesso.");
      await refresh?.();
      setOpenOS(null);
    } catch (e: any) {
      window.alert(`Falha ao dar baixa no item: ${e?.message || e}`);
    } finally {
      setBusy(false);
    }
  }, [
    openOS,
    osDetails,
    baixarItemEnabled,
    ensureRawForOS,
    ensureMeId,
    refresh,
    selectedIndex,
    invalidateProdutos,
  ]);

  const handleMarkOsPendente = useCallback(async () => {
    if (!openOS || busy) return;

    try {
      setBusy(true);
      const data = await ensureRawForOS(openOS);
      if (!data || !data.length)
        return window.alert("Dados da O.S. indisponíveis.");

      const pick = [...data].sort((a, b) => {
        const ta = new Date(a.movimentacao.criado_em).getTime();
        const tb = new Date(b.movimentacao.criado_em).getTime();
        if (tb !== ta) return tb - ta;
        return (b.movimentacao.id || 0) - (a.movimentacao.id || 0);
      })[0];

      const movimentacaoId = pick?.movimentacao?.id;
      if (!movimentacaoId)
        return window.alert(
          "Não foi possível identificar a movimentação desta O.S."
        );

      const uid = await ensureMeId();
      if (uid == null)
        return window.alert(
          "Sessão não encontrada. Faça login para prosseguir."
        );

      await postJSON(URL_AJUSTAR_PENDENTE, {
        movimentacao_id: movimentacaoId,
        responsavel_id: uid,
      });
      await invalidateProdutos();

      window.alert(
        openOS && isOsPendente(openOS)
          ? "O.S. desmarcada como 'Pendente para Retorno'."
          : "O.S. marcada como 'Pendente para Retorno'."
      );
      await refresh?.();
    } catch (e: any) {
      window.alert(`Falha ao alternar pendência da O.S.: ${e?.message || e}`);
    } finally {
      setBusy(false);
    }
  }, [
    openOS,
    busy,
    ensureRawForOS,
    ensureMeId,
    refresh,
    invalidateProdutos,
    isOsPendente,
  ]);

  const handleModalClose = useCallback(() => {
    if (!busy) setOpenOS(null);
  }, [busy]);

  // === passar pendência para o Table ===
  const isRowPending = useCallback(
    (rowData: (string | number | React.ReactNode)[]) => {
      const os = getOsFromDisplayRowForPending(rowData as Row);
      return os ? isOsPendente(String(os)) : false;
    },
    [getOsFromDisplayRowForPending, isOsPendente]
  );

  // === agrupamento visual: "Minhas O.S." vs "O.S. de outros coordenadores" ===
  const getRowGroupForTable = useCallback(
    (rowData: (string | number | React.ReactNode)[]) => {
      if (!meuNomeCoordenador) return "others";
      const respCell = rowData[IDX.RESP_COORD];
      const resp =
        typeof respCell === "string" ? respCell : String(respCell ?? "");
      if (!resp.trim()) return "others";
      return resp.trim().toLowerCase() === meuNomeCoordenador.toLowerCase()
        ? "mine"
        : "others";
    },
    [meuNomeCoordenador]
  );

  return (
    <div>
      <TitleButtons title="Coordenação - Standby" />

      <Table
        headers={headers}
        rows={tableDisplayRows}
        columnWidths={columnWidths}
        isTableStandby
        onDoubleClick={handleDoubleClick}
        enableCellColors={false}
        isRowPending={(row) =>
          isRowPending(row as (string | number | React.ReactNode)[])
        }
        atrasoColumnIndex={8}
        responsiveWidths={{
          1300: {
            1: "110px",
            2: "0px",
            3: "300px",
            4: "0px",
            5: "100px",
            6: "100px",
            7: "60px",
            8: "60px",
          },
        }}
        getRowGroup={(rowData) => getRowGroupForTable(rowData)}
        groupLabels={{
          mine: "Minhas O.S.",
          others: "O.S. de outros coordenadores",
        }}
      />

      <Modal open={!!openOS} onClose={handleModalClose} maxWidth={1280}>
        {openOS && osDetails && (
          <div
            style={{
              padding: 0,
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: 8,
              width: "100%",
            }}
          >
            {openOS && isOsPendente(openOS) && (
              <div
                style={{
                  background: "#FFF3C4",
                  border: "1px solid #F6C453",
                  padding: "10px 12px",
                  marginBottom: 4,
                  borderRadius: 6,
                  fontSize: 14,
                }}
              >
                Esta O.S. está <b>marcada como “Pendente para Retorno”</b>.
              </div>
            )}

            <div style={{ width: "100%" }}>
              <LeftTable data={[...osDetails.info]} />
            </div>

            <AuvoReportsSection os={openOS} />

            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                marginTop: 4,
              }}
            >
              <button
                aria-disabled={!(!busy && openOS)}
                onClick={handleMarkOsPendente}
                style={{
                  padding: "11px 12px",
                  borderRadius: 2,
                  userSelect: "none",
                  transition: "filter .12s, transform .06s",
                  background: "#FFF7ED",
                  border: "1px solid #D97706",
                  color: "#92400E",
                  cursor: !!openOS && !busy ? "pointer" : "default",
                }}
                title={
                  openOS && isOsPendente(openOS)
                    ? "Remove a marcação de 'Pendente para Retorno' desta O.S."
                    : "Alterna a marcação da O.S. como 'Pendente para Retorno'"
                }
              >
                {openOS && isOsPendente(openOS)
                  ? "Desmarcar O.S. como Pendente para Retorno"
                  : "Marcar O.S. como Pendente para Retorno"}
              </button>

              {/* ✅ "Dar baixa na O.S." aparece se for minha OU se for DEV */}
              {podeBaixarEstaOS && !(openOS && isOsPendente(openOS)) && (
                <button
                  aria-disabled={!(!busy && openOS)}
                  onClick={handleBaixarOSInteira}
                  style={{
                    padding: "11px 12px",
                    borderRadius: 2,
                    userSelect: "none",
                    transition: "filter .12s, transform .06s",
                    background: "#FDEAEA6B",
                    border: "1px solid #991b1b",
                    color: "#991b1b",
                    cursor: !!openOS && !busy ? "pointer" : "default",
                  }}
                  title="Dá baixa em todos os itens desta O.S."
                >
                  Dar baixa na O.S.
                </button>
              )}
            </div>

            <em style={{ color: "#6b7280", marginTop: 2 }}>
              Ao marcar como <strong>Pendente para Retorno</strong>, você
              sinaliza que a baixa foi finalizada e o técnico deve{" "}
              <strong>retornar o restante dos materiais</strong> ao Suprimentos.
            </em>

            <ModalItemsGrid
              headers={osDetails.headers}
              colWidths={osDetails.colWidths}
              rows={osDetails.tableRows}
              selectedIndex={selectedIndex}
              onSelectIndex={setSelectedIndex}
              centerCols={[0, 1, 2, 3, 4, 5, 7, 8]}
              perColumnRender={{
                "Preço de Venda": (uiRow) => {
                  let precoDisplay: string | React.ReactNode = "-";
                  if (rawMovs && rawMovs.length) {
                    const found = mapUiToRawItem(rawMovs, uiRow as Row);
                    const pv = found?.preco_venda;
                    const moeda = String(found?.moeda || "BRL").toUpperCase();
                    if (
                      pv === "pendente informar" ||
                      String(pv).toLowerCase() === "pendente informar"
                    ) {
                      precoDisplay = "pendente informar";
                    } else if (pv != null && pv !== "") {
                      const n = Number(String(pv).replace(",", "."));
                      if (Number.isFinite(n)) {
                        try {
                          precoDisplay = new Intl.NumberFormat("pt-BR", {
                            style: "currency",
                            currency: moeda,
                          }).format(n);
                        } catch {
                          precoDisplay = `R$ ${n.toLocaleString("pt-BR", {
                            minimumFractionDigits: 2,
                          })}`;
                        }
                      }
                    }
                  }
                  return precoDisplay;
                },
                Moeda: (uiRow) => {
                  if (rawMovs && rawMovs.length) {
                    const found = mapUiToRawItem(rawMovs, uiRow as Row);
                    return String(found?.moeda || "BRL").toUpperCase();
                  }
                  return "-";
                },
              }}
            />

            {/* ✅ "Dar baixa no item" aparece se for minha OU se for DEV */}
            {podeBaixarEstaOS && !osIsPendente && (
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                  marginTop: 8,
                }}
              >
                <button
                  aria-disabled={!(selectedIndex != null && !busy)}
                  onClick={handleBaixarItem}
                  style={{
                    padding: "11px 12px",
                    borderRadius: 2,
                    userSelect: "none",
                    transition: "filter .12s, transform .06s",
                    background: "#FDEAEA6B",
                    border: "1px solid #991b1b",
                    color: "#991b1b",
                    cursor:
                      selectedIndex != null && !busy ? "pointer" : "default",
                  }}
                  title="Dá baixa somente no item selecionado"
                >
                  Dar baixa no item
                </button>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CoordenacaoStandby;
